import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { Document } from "../../../types";

interface RenameFolderDialogProps {
  open: boolean;
  onClose: () => void;
  folder: Document | null;
  onRenameFolder: (folderId: string, newName: string) => Promise<void>;
  loading?: boolean;
}

export default function RenameFolderDialog({
  open,
  onClose,
  folder,
  onRenameFolder,
  loading = false,
}: RenameFolderDialogProps) {
  const [newName, setNewName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Combined loading state to prevent race conditions
  const isLoading = loading || isSubmitting;

  // Reset form when dialog opens with a new folder
  useEffect(() => {
    if (open && folder) {
      setNewName(folder.name || "");
      setError(null);
    }
  }, [open, folder]);

  const handleClose = () => {
    if (!isLoading) {
      setNewName("");
      setError(null);
      onClose();
    }
  };

  const validateName = (name: string): string | null => {
    if (!name.trim()) {
      return "Folder name is required";
    }
    if (name.includes("/")) {
      return "Folder name cannot contain slashes";
    }
    if (name.trim() !== name) {
      return "Folder name cannot have leading or trailing spaces";
    }
    if (name === folder?.name) {
      return "New name must be different from current name";
    }
    return null;
  };

  const handleRename = async () => {
    const validationError = validateName(newName);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!folder || isLoading) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onRenameFolder(folder.id, newName.trim());
      setNewName("");
      handleClose();
    } catch (err: any) {
      setError(err.message || "Failed to rename folder");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading && newName.trim()) {
      handleRename();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Rename Folder
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Enter a new name for the folder "{folder?.name}". All documents and
          subfolders will be updated automatically.
        </Typography>
        <TextField
          fullWidth
          label="New Folder Name"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          autoFocus
          disabled={isLoading}
          error={!!error}
          helperText={error}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleRename}
          loading={isLoading}
          disabled={!newName.trim() || newName === folder?.name || isLoading}
        >
          Rename
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
